import { useState, useEffect, useCallback, useRef } from 'react';
import { ordersService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { initialOrders } from '../data/orders';
import { mergeOrderPreferComplete, ordersListSignature, normalizeOrderRecord, computeOrderLiveEligible } from '../lib/orderUtils';
import type { Order } from '../types';

interface UseOrdersReturn {
  orders: Order[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (order: Omit<Order, 'id'>, options?: { preferredId?: string }) => Promise<Order>;
  update: (id: string, updates: Partial<Order> | ((currentOrder: Order) => Partial<Order>)) => Promise<Order>;
  remove: (id: string) => Promise<void>;
  getById: (id: string) => Order | undefined;
}

export const useOrders = (enabled = true): UseOrdersReturn => {
  const [orders, setOrders] = useState<Order[]>(() => (getDataSource() === 'mock' ? initialOrders : []));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ordersSigRef = useRef('');

  const fetchOrders = useCallback(async () => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      setOrders(initialOrders);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await ordersService.getRecent({ preferServer: true });
      setOrders((prev) => {
        const orderMap = new Map<string, Order>();
        for (const order of prev) {
          if (!order.isDeleted) orderMap.set(order.id, order);
        }
        for (const order of data) {
          if (order.isDeleted) {
            orderMap.delete(order.id);
            continue;
          }
          orderMap.set(order.id, mergeOrderPreferComplete(orderMap.get(order.id), order));
        }
        const merged = Array.from(orderMap.values());
        const sig = ordersListSignature(merged);
        if (sig === ordersSigRef.current) return prev;
        ordersSigRef.current = sig;
        return merged;
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load orders';
      setError(errorMessage);
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      void fetchOrders();
      return;
    }

    let cleanupFn: (() => void) | null = null;

    if (dataSource === 'firebase') {
      setLoading(true);
      setError(null);

      const unsubscribe = ordersService.subscribe((updatedOrders) => {
        setOrders((prev) => {
          const liveIds = new Set(updatedOrders.map((o) => o.id));
          const orderMap = new Map<string, Order>();

          for (const order of updatedOrders) {
            if (order.isDeleted) continue;
            const normalized = normalizeOrderRecord(order);
            const existing = prev.find((p) => p.id === order.id);
            orderMap.set(
              order.id,
              existing ? mergeOrderPreferComplete(existing, normalized) : normalized,
            );
          }

          // Keep briefly-local tickets that still compute as live (create race before snapshot).
          for (const order of prev) {
            if (liveIds.has(order.id) || order.isDeleted) continue;
            if (computeOrderLiveEligible(order)) {
              orderMap.set(order.id, order);
            }
          }

          const merged = Array.from(orderMap.values()).filter((o) => !o.isDeleted);
          const sig = ordersListSignature(merged);
          if (sig === ordersSigRef.current) return prev;
          ordersSigRef.current = sig;
          return merged;
        });
        setLoading(false);
      });

      if (unsubscribe) {
        cleanupFn = () => unsubscribe();
      }
    }

    return () => {
      if (cleanupFn) cleanupFn();
    };
  }, [fetchOrders, enabled]);

  const create = useCallback(async (order: Omit<Order, 'id'>, options?: { preferredId?: string }): Promise<Order> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const newOrder: Order = {
        ...order,
        id: options?.preferredId || `ORD-${Date.now()}`,
      };
      setOrders(prev => [...prev, newOrder]);
      return newOrder;
    }

    setError(null);
    try {
      const newOrder = await ordersService.create(order, options);
      // ordersService.create already merges prepared over thin read-back + normalizes.
      setOrders((prev) => {
        const orderMap = new Map<string, Order>();
        for (const existing of prev) {
          if (!existing.isDeleted) orderMap.set(existing.id, existing);
        }
        const existing = orderMap.get(newOrder.id);
        orderMap.set(
          newOrder.id,
          existing ? mergeOrderPreferComplete(existing, newOrder) : newOrder,
        );
        const merged = Array.from(orderMap.values());
        ordersSigRef.current = ordersListSignature(merged);
        return merged;
      });
      return newOrder;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create order';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const update = useCallback(async (id: string, updates: Partial<Order> | ((currentOrder: Order) => Partial<Order>)): Promise<Order> => {
    const dataSource = getDataSource();
    
    let originalOrder: Order | undefined;
    let optimisticOrder: Order;
    let resolvedUpdates: Partial<Order>;
    
    setOrders(prev => {
      const existingOrder = prev.find(o => o.id === id);
      if (!existingOrder) {
        return prev;
      }
      originalOrder = existingOrder;

      if (typeof updates === 'function') {
        resolvedUpdates = updates(existingOrder);
      } else {
        resolvedUpdates = updates;
      }

      optimisticOrder = { ...existingOrder, ...resolvedUpdates } as Order;
      const next = prev.map(o => o.id === id ? optimisticOrder : o);
      ordersSigRef.current = ordersListSignature(next);
      return next;
    });

    if (!originalOrder) {
      if (dataSource !== 'mock') {
        setError(null);
        try {
          const remote = await ordersService.getById(id, { preferServer: true });
          if (!remote || remote.isDeleted) {
            return Promise.resolve({} as Order);
          }
          resolvedUpdates =
            typeof updates === 'function' ? updates(remote) : updates;
          if (!resolvedUpdates || Object.keys(resolvedUpdates).length === 0) {
            return remote;
          }
          const updated = await ordersService.update(id, resolvedUpdates);
          setOrders((prev) => {
            const map = new Map(prev.map((o) => [o.id, o]));
            map.set(id, updated);
            const merged = Array.from(map.values());
            ordersSigRef.current = ordersListSignature(merged);
            return merged;
          });
          return updated;
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to update order';
          setError(errorMessage);
          console.error(`❌ Failed to update order ${id} (remote fallback):`, err);
          throw err;
        }
      }
      return Promise.resolve({} as Order);
    }
    
    if (dataSource === 'mock') {
      return optimisticOrder!;
    }

    setError(null);
    
    try {
      await ordersService.update(id, resolvedUpdates!);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update order';
      setError(errorMessage);
      console.error(`❌ Failed to update order ${id}:`, err);
      setOrders(prev => prev.map(o => o.id === id ? originalOrder! : o));
      throw err;
    }
    
    return optimisticOrder!;
  }, []);

  const remove = useCallback(async (id: string): Promise<void> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      setOrders(prev => prev.filter(o => o.id !== id));
      return;
    }

    setError(null);
    try {
      await ordersService.delete(id);
      setOrders(prev => {
        const next = prev.filter(o => o.id !== id);
        ordersSigRef.current = ordersListSignature(next);
        return next;
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete order';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const getById = useCallback((id: string): Order | undefined => {
    return orders.find(o => o.id === id);
  }, [orders]);

  return {
    orders,
    loading,
    error,
    refresh: fetchOrders,
    create,
    update,
    remove,
    getById,
  };
};
